import React, { useState, useRef, useEffect } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Building2, ChevronRight, Plus, X, UserCircle, Package, Users, CreditCard, LayoutTemplate, History } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Organisation, OrganisationKind } from '../index'

interface OrganisationSidebarProps {
  organisations: Organisation[]
  selectedOrgId: number | null
  onSelect: (orgId: number) => void
  onOrganisationCreated?: () => void
  avatarsSelected?: boolean
  onAvatarsSelect?: () => void
  modulesSelected?: boolean
  onModulesSelect?: () => void
  usersSelected?: boolean
  onUsersSelect?: () => void
  paymentsSelected?: boolean
  onPaymentsSelect?: () => void
  planCardsSelected?: boolean
  onPlanCardsSelect?: () => void
  activitySelected?: boolean
  onActivitySelect?: () => void
}

const CREATE_ORGANISATION = gql`
  mutation createOrganisation($input: CreateOrganisationInput!) {
    createOrganisation(input: $input) {
      id
      name
      description
      kind
      isActive
    }
  }
`

const OrganisationSidebar: React.FC<OrganisationSidebarProps> = ({
  organisations,
  selectedOrgId,
  onSelect,
  onOrganisationCreated,
  avatarsSelected,
  onAvatarsSelect,
  modulesSelected,
  onModulesSelect,
  usersSelected,
  onUsersSelect,
  paymentsSelected,
  onPaymentsSelect,
  planCardsSelected,
  onPlanCardsSelect,
  activitySelected,
  onActivitySelect,
}) => {
  const [showModal, setShowModal] = useState(false)
  const [orgName, setOrgName] = useState('')
  const [orgDescription, setOrgDescription] = useState('')
  const [orgKind, setOrgKind] = useState<OrganisationKind>('b2b')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        handleCloseModal()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showModal])

  const handleCloseModal = () => {
    setShowModal(false)
    setOrgName('')
    setOrgDescription('')
    setOrgKind('b2b')
    setError(null)
  }

  const handleCreateOrganisation = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgName.trim()) {
      setError('Organisation name is required')
      return
    }

    try {
      setSubmitting(true)
      setError(null)
      const response = await authorizedGraphQLRequest<{
        createOrganisation: Organisation
      }>(CREATE_ORGANISATION, {
        input: {
          name: orgName.trim(),
          description: orgDescription.trim() || null,
          kind: orgKind,
        },
      })

      handleCloseModal()
      if (onOrganisationCreated) {
        onOrganisationCreated()
      }
      // Select the newly created organisation
      if (response.createOrganisation?.id) {
        onSelect(response.createOrganisation.id)
      }
    } catch (err: any) {
      console.error('Failed to create organisation:', err)
      const message = err?.response?.errors?.[0]?.message || 'Failed to create organisation'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCloseModal()
    }
  }

  return (
    <>
      <div className="w-72 fixed top-16 left-0 h-[calc(100vh-4rem)] bg-gradient-to-b from-[#004D43] to-[#193133] shadow-xl overflow-y-auto">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organisations
          </h3>
          <button onClick={() => setShowModal(true)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors" title="Create Organisation">
            <Plus className="h-4 w-4" />
          </button>
        </div>

        <div className="p-2">
          {organisations.length === 0 ? (
            <div className="text-center text-teal-200 py-8">
              <p className="text-sm">No organisations found</p>
            </div>
          ) : (
            <ul className="space-y-1">
              {[...organisations]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((org) => {
                  const isSelected = org.id === selectedOrgId
                  return (
                    <li key={org.id}>
                      <button
                        onClick={() => onSelect(org.id)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${
                          isSelected ? 'bg-[#E3FFAB]/20 text-[#E3FFAB] shadow-md' : 'text-teal-50 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className={`w-2 h-2 rounded-full ${org.isActive ? 'bg-green-400' : 'bg-gray-400'}`} />
                          <span className="truncate font-medium">{org.name}</span>
                          {org.kind === 'b2c' && <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#E3FFAB]/20 text-[#E3FFAB]">B2C</span>}
                        </div>
                        {isSelected && <ChevronRight className="h-4 w-4 flex-shrink-0" />}
                      </button>
                    </li>
                  )
                })}
            </ul>
          )}
        </div>

        <div className="p-2 border-t border-white/10 mt-2 space-y-1">
          <button
            onClick={onAvatarsSelect}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              avatarsSelected ? 'bg-[#E3FFAB]/20 text-[#E3FFAB] shadow-md' : 'text-teal-50 hover:bg-white/10'
            }`}
          >
            <UserCircle className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Avatars</span>
          </button>
          <button
            onClick={onModulesSelect}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              modulesSelected ? 'bg-[#E3FFAB]/20 text-[#E3FFAB] shadow-md' : 'text-teal-50 hover:bg-white/10'
            }`}
          >
            <Package className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Modules</span>
          </button>
          <button
            onClick={onUsersSelect}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              usersSelected ? 'bg-[#E3FFAB]/20 text-[#E3FFAB] shadow-md' : 'text-teal-50 hover:bg-white/10'
            }`}
          >
            <Users className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Users</span>
          </button>
          <button
            onClick={onPaymentsSelect}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              paymentsSelected ? 'bg-[#E3FFAB]/20 text-[#E3FFAB] shadow-md' : 'text-teal-50 hover:bg-white/10'
            }`}
          >
            <CreditCard className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Payment accounts</span>
          </button>
          <button
            onClick={onPlanCardsSelect}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              planCardsSelected ? 'bg-[#E3FFAB]/20 text-[#E3FFAB] shadow-md' : 'text-teal-50 hover:bg-white/10'
            }`}
          >
            <LayoutTemplate className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Plan cards</span>
          </button>
          <button
            onClick={onActivitySelect}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              activitySelected ? 'bg-[#E3FFAB]/20 text-[#E3FFAB] shadow-md' : 'text-teal-50 hover:bg-white/10'
            }`}
          >
            <History className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Activity</span>
          </button>
        </div>
      </div>

      {/* Create Organisation Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50" onClick={handleOutsideClick}>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/40" />

          {/* Modal */}
          <div ref={modalRef} className="relative bg-white rounded-xl shadow-xl w-full max-w-md animate-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">Create Organisation</h2>
              <button onClick={handleCloseModal} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <form onSubmit={handleCreateOrganisation} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Organisation name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoFocus
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={orgDescription}
                  onChange={(e) => setOrgDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kind</label>
                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      { value: 'b2b', label: 'B2B', hint: 'Subscriptions and seats' },
                      { value: 'b2c', label: 'B2C', hint: 'Join links and minutes' },
                    ] as { value: OrganisationKind; label: string; hint: string }[]
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setOrgKind(option.value)}
                      className={`px-4 py-2 border rounded-lg text-left transition-colors ${orgKind === option.value ? 'border-primary bg-primary/5 ring-2 ring-primary' : 'border-gray-300 hover:bg-gray-50'}`}
                    >
                      <span className="block text-sm font-medium text-gray-800">{option.label}</span>
                      <span className="block text-xs text-gray-500">{option.hint}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">The kind cannot be changed after the organisation is created.</p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default OrganisationSidebar
